import React, { useEffect, useRef, useState, memo, useCallback, useMemo } from 'react';

// CanvasCroppedImage - renders an exact pixel crop using Canvas drawImage
// Props:
// - src: image URL
// - bbox: {x1,y1,x2,y2} in pixels
// - className: optional wrapper classes; canvas fills 100%
// - onLoad/onError: optional callbacks
// Global image cache to avoid reloading the same image
const imageCache = new Map();

function CanvasCroppedImage({ src, bbox, className = '', onLoad, onError, alt = '' }) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  // Store callbacks in refs to avoid infinite loops from dependency array
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  // Use ref to track loading state to avoid dependency issues
  const isLoadingRef = useRef(false);
  
  // Update refs when callbacks change
  useEffect(() => {
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
  }, [onLoad, onError]);

  // Debounced container size update to reduce redraws
  const updateContainerSize = useCallback(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const newSize = { width: el.clientWidth, height: el.clientHeight };
    
    // Only update if size actually changed
    setContainerSize(prev => {
      if (prev.width === newSize.width && prev.height === newSize.height) {
        return prev;
      }
      return newSize;
    });
  }, []);

  // Observe container size with debouncing
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    
    // Initial measurement
    updateContainerSize();
    
    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateContainerSize, 16); // ~60fps
    };
    
    let ro;
    try {
      ro = new ResizeObserver(debouncedUpdate);
      ro.observe(el);
    } catch (_) {
      window.addEventListener('resize', debouncedUpdate);
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (ro) {
        try { ro.unobserve(el); } catch(_) {}
      } else {
        window.removeEventListener('resize', debouncedUpdate);
      }
    };
  }, [updateContainerSize]);

  // Load image with caching to avoid reloading
  useEffect(() => {
    if (!src) return;
    
    // Check cache first
    if (imageCache.has(src)) {
      const cachedImg = imageCache.get(src);
      setImg(cachedImg);
      if (onLoadRef.current) onLoadRef.current();
      return;
    }
    
    // If already loading this image, don't start another load
    if (isLoadingRef.current) return;
    
    let revoked = false;
    let objectUrl = null;
    
    isLoadingRef.current = true;
    
    async function load() {
      try {
        // Try fetch -> blob -> ImageBitmap for cross-origin safety
        const res = await fetch(src, { credentials: 'omit' });
        const blob = await res.blob();
        // Create a display-safe object URL for <img> fallback if needed
        objectUrl = URL.createObjectURL(blob);
        
        // Create bitmap for fast decode (no crop yet; crop at draw time with sx,sy,sw,sh or later step)
        if ('createImageBitmap' in window) {
          const bitmap = await createImageBitmap(blob);
          if (revoked) {
            try { bitmap.close && bitmap.close(); } catch(_) {}
            return;
          }
          
          // Cache the bitmap
          imageCache.set(src, bitmap);
          setImg(bitmap);
          if (onLoadRef.current) onLoadRef.current();
        } else {
          // Fallback: classic Image element
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = (e) => { 
            if (!revoked) { 
              // Cache the image
              imageCache.set(src, image);
              setImg(image); 
              if (onLoadRef.current) onLoadRef.current(e); 
            } 
          };
          image.onerror = (e) => { 
            if (onErrorRef.current) onErrorRef.current(e); 
          };
          image.src = objectUrl;
        }
      } catch (e) {
        if (onErrorRef.current) onErrorRef.current(e);
      } finally {
        isLoadingRef.current = false;
      }
    }
    
    load();
    
    return () => {
      revoked = true;
      isLoadingRef.current = false;
      try { if (objectUrl) URL.revokeObjectURL(objectUrl); } catch(_) {}
    };
  }, [src]);

  // Memoize drawing calculations to avoid unnecessary redraws
  const drawParams = useMemo(() => {
    if (!img || !bbox || !containerSize.width || !containerSize.height) return null;

    const imgWidth = img.width || img.naturalWidth || 0;
    const imgHeight = img.height || img.naturalHeight || 0;
    if (!imgWidth || !imgHeight) return null;

    const toNumber = (value) => (typeof value === 'number' ? value : Number(value));
    const isValidNumber = (value) => Number.isFinite(value);

    const normalizeBbox = (bboxInput) => {
      if (!bboxInput || typeof bboxInput !== 'object') return null;

      let x1;
      let y1;
      let x2;
      let y2;

      if (isValidNumber(toNumber(bboxInput.x1)) && isValidNumber(toNumber(bboxInput.y1))) {
        x1 = toNumber(bboxInput.x1);
        y1 = toNumber(bboxInput.y1);
        x2 = toNumber(bboxInput.x2);
        y2 = toNumber(bboxInput.y2);
      } else if (isValidNumber(toNumber(bboxInput.min_x)) && isValidNumber(toNumber(bboxInput.min_y))) {
        x1 = toNumber(bboxInput.min_x);
        y1 = toNumber(bboxInput.min_y);
        x2 = toNumber(bboxInput.max_x);
        y2 = toNumber(bboxInput.max_y);
      } else if (isValidNumber(toNumber(bboxInput.x)) && isValidNumber(toNumber(bboxInput.y))) {
        const width = isValidNumber(toNumber(bboxInput.width))
          ? toNumber(bboxInput.width)
          : toNumber(bboxInput.w);
        const height = isValidNumber(toNumber(bboxInput.height))
          ? toNumber(bboxInput.height)
          : toNumber(bboxInput.h);
        x1 = toNumber(bboxInput.x);
        y1 = toNumber(bboxInput.y);
        x2 = x1 + width;
        y2 = y1 + height;
      } else if (Array.isArray(bboxInput) && bboxInput.length >= 4) {
        x1 = toNumber(bboxInput[0]);
        y1 = toNumber(bboxInput[1]);
        x2 = toNumber(bboxInput[2]);
        y2 = toNumber(bboxInput[3]);
      }

      if (![x1, y1, x2, y2].every(isValidNumber)) return null;

      const maxCoord = Math.max(x1, y1, x2, y2);
      const isNormalized = maxCoord <= 1;
      if (isNormalized) {
        return {
          x1: x1 * imgWidth,
          y1: y1 * imgHeight,
          x2: x2 * imgWidth,
          y2: y2 * imgHeight,
        };
      }

      return { x1, y1, x2, y2 };
    };

    const normalizedBbox = normalizeBbox(bbox);
    if (!normalizedBbox) return null;

    const x = Math.max(0, Math.floor(normalizedBbox.x1));
    const y = Math.max(0, Math.floor(normalizedBbox.y1));
    const w = Math.max(1, Math.floor(normalizedBbox.x2 - normalizedBbox.x1));
    const h = Math.max(1, Math.floor(normalizedBbox.y2 - normalizedBbox.y1));

    const CW = Math.max(1, containerSize.width || 0);
    const CH = Math.max(1, containerSize.height || 0);
    const cropAspect = w / h;
    const containerAspect = CW / CH;
    
    // Calculate how to fit the crop into the container while preserving aspect ratio
    let drawW, drawH, offsetX = 0, offsetY = 0;
    
    if (cropAspect > containerAspect) {
      // Crop is wider than container - fit to height, crop width
      drawH = CH;
      drawW = CH * cropAspect;
      offsetX = (CW - drawW) / 2;
    } else {
      // Crop is taller than container - fit to width, crop height  
      drawW = CW;
      drawH = CW / cropAspect;
      offsetY = (CH - drawH) / 2;
    }
    
    return { x, y, w, h, CW, CH, drawW, drawH, offsetX, offsetY };
  }, [img, bbox, containerSize]);

  // Draw crop to canvas with optimized rendering
  useEffect(() => {
    if (!drawParams || !canvasRef.current) return;
    
    const { x, y, w, h, CW, CH, drawW, drawH, offsetX, offsetY } = drawParams;
    const canvas = canvasRef.current;

    // Only resize canvas if dimensions changed
    if (canvas.width !== CW || canvas.height !== CH) {
      canvas.width = CW;
      canvas.height = CH;
    }

    const ctx = canvas.getContext('2d');
    
    // Use lower quality for better performance during animations
    ctx.imageSmoothingQuality = 'medium';
    ctx.clearRect(0, 0, CW, CH);
    
    // If ImageBitmap has been closed by React unmount/mount cycle, skip drawing
    if (img && typeof img.close === 'function') {
      try {
        // drawImage throws if bitmap is closed; catch and skip
        ctx.drawImage(img, x, y, w, h, offsetX, offsetY, drawW, drawH);
      } catch (e) {
        // No-op; a new effect will run when a fresh bitmap arrives
      }
    } else {
      try {
        ctx.drawImage(img, x, y, w, h, offsetX, offsetY, drawW, drawH);
      } catch (e) {
        // Fallback: draw whole image if source rect fails
        try { ctx.drawImage(img, 0, 0, img.width || CW, img.height || CH, offsetX, offsetY, drawW, drawH); } catch(_) {}
      }
    }
  }, [drawParams, img]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`} style={{ width: '100%', height: '100%' }} aria-label={alt}>
      <canvas 
        ref={canvasRef} 
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'block'
        }} 
      />
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(CanvasCroppedImage);


